import { useMemo } from 'react';

/**
 * Line chart in the BodyMax analytics style: grey plot, white crossing gridlines,
 * dashed target line, one coloured series, tooltip pinned to the selected point.
 * Hand-rolled SVG so it stays sharp at 360px and adds nothing to the bundle.
 */
const PAD = { top: 18, right: 14, bottom: 26, left: 42 };

export const RecoveryChart = ({
  points, target, color, formatValue, formatAxis, formatLabel,
  selected, onSelect, height = 220, tooltip,
}) => {
  const W = 340;
  const H = height;
  const plot = { x: PAD.left, y: PAD.top, w: W - PAD.left - PAD.right, h: H - PAD.top - PAD.bottom };

  const { scaleX, scaleY, ticks, path, area, dots, targetPath } = useMemo(() => {
    const values = points.map((p) => p.value).filter((v) => v !== null && v !== undefined);
    const dayTargets = points.map((p) => (p.target ?? target) || 0);
    const top = Math.max(target || 0, ...dayTargets, ...(values.length ? values : [1])) * 1.15 || 1;
    const sx = (i) => plot.x + (points.length > 1 ? (i / (points.length - 1)) * plot.w : plot.w / 2);
    const sy = (v) => plot.y + plot.h - (v / top) * plot.h;
    const step = top / 4;
    const tickValues = [0, step, step * 2, step * 3, step * 4];

    const drawn = points.map((p, i) => (p.value === null || p.value === undefined
      ? null : { i, x: sx(i), y: sy(p.value), ...p }));
    // Break the line across days with no data rather than inventing a value.
    const segments = [];
    let run = [];
    drawn.forEach((d) => { if (d) { run.push(d); } else if (run.length) { segments.push(run); run = []; } });
    if (run.length) segments.push(run);

    const line = segments.map((seg) => seg.map((d, k) => `${k ? 'L' : 'M'}${d.x},${d.y}`).join(' ')).join(' ');
    const fill = segments.filter((s) => s.length > 1).map((seg) => (
      `M${seg[0].x},${plot.y + plot.h} ` + seg.map((d) => `L${d.x},${d.y}`).join(' ')
      + ` L${seg[seg.length - 1].x},${plot.y + plot.h} Z`
    )).join(' ');

    // Targets can change with weight or goal, so the line steps where theirs did —
    // but only for a change big enough to matter (>2%).
    const spread = Math.max(...dayTargets) / Math.max(Math.min(...dayTargets), 1);
    const varies = spread > 1.02;
    const tPath = !target ? '' : varies
      ? (() => {
        // One continuous step line with risers, so a target change reads as a step.
        const half = points.length > 1 ? plot.w / (points.length - 1) / 2 : plot.w / 2;
        let d = `M${plot.x},${sy(dayTargets[0])}`;
        dayTargets.forEach((t, i) => {
          const edge = Math.min(sx(i) + half, plot.x + plot.w);
          d += ` L${edge},${sy(t)}`;
          if (i < dayTargets.length - 1) d += ` L${edge},${sy(dayTargets[i + 1])}`;
        });
        return d;
      })()
      : `M${plot.x},${sy(dayTargets[dayTargets.length - 1] || target)} L${plot.x + plot.w},${sy(dayTargets[dayTargets.length - 1] || target)}`;

    return { scaleX: sx, scaleY: sy, ticks: tickValues, path: line, area: fill,
             dots: drawn.filter(Boolean), targetPath: tPath };
  }, [points, target, plot.x, plot.y, plot.w, plot.h]);

  const sel = dots.find((d) => d.i === selected) || dots[dots.length - 1];
  const selTarget = sel ? (sel.target ?? target) : target;
  const gradId = `grad-${color.replace('#', '')}`;

  // Keep the tooltip inside the plot on narrow screens.
  const tipW = 118;
  const tipX = sel ? Math.min(Math.max(sel.x - tipW / 2, plot.x + 2), plot.x + plot.w - tipW - 2) : 0;
  const tipAbove = sel ? sel.y > plot.y + 70 : true;
  const tipY = sel ? (tipAbove ? sel.y - 66 : sel.y + 14) : 0;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ touchAction: 'pan-y' }}
         data-testid="recovery-analytics-chart">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      <rect x={plot.x} y={plot.y} width={plot.w} height={plot.h} fill="#2B2F35" rx="4" />

      {ticks.map((t, i) => (
        <g key={`h${i}`}>
          <line x1={plot.x} x2={plot.x + plot.w} y1={scaleY(t)} y2={scaleY(t)}
                stroke="#FFFFFF" strokeOpacity={i === 0 ? 0.35 : 0.18} strokeWidth="1" />
          <text x={plot.x - 6} y={scaleY(t) + 3.5} textAnchor="end" fontSize="11" fill="#9CA3AF">
            {formatAxis(t)}
          </text>
        </g>
      ))}
      {points.map((p, i) => (
        <line key={`v${i}`} x1={scaleX(i)} x2={scaleX(i)} y1={plot.y} y2={plot.y + plot.h}
              stroke="#FFFFFF" strokeOpacity="0.14" strokeWidth="1" />
      ))}

      {targetPath && (
        <path d={targetPath} fill="none" stroke="#E5E7EB" strokeWidth="1.5" strokeDasharray="7 5"
              data-testid="chart-target-line" />
      )}

      {area && <path d={area} fill={`url(#${gradId})`} />}
      {path && <path d={path} fill="none" stroke={color} strokeWidth="2.5"
                     strokeLinecap="round" strokeLinejoin="round" />}

      {points.map((p, i) => (p.change ? (
        <g key={`c${i}`} data-testid={`chart-target-change-${p.date}`}>
          <line x1={scaleX(i) - (points.length > 1 ? plot.w / (points.length - 1) / 2 : 0)}
                x2={scaleX(i) - (points.length > 1 ? plot.w / (points.length - 1) / 2 : 0)}
                y1={plot.y} y2={plot.y + plot.h}
                stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="2 3" />
          <circle cx={scaleX(i) - (points.length > 1 ? plot.w / (points.length - 1) / 2 : 0)}
                  cy={plot.y + 4} r="3" fill="#FFFFFF" />
        </g>
      ) : null))}

      {dots.map((d) => (
        <g key={d.date} onClick={() => onSelect(d.i)} style={{ cursor: 'pointer' }}>
          <rect x={d.x - 16} y={plot.y} width="32" height={plot.h} fill="transparent" />
          <circle cx={d.x} cy={d.y} r={d.i === sel?.i ? 6 : 4}
                  fill={d.source === 'auto' ? '#0B0D10' : color}
                  stroke={d.i === sel?.i ? '#FFFFFF' : color}
                  strokeWidth={d.i === sel?.i ? 2.5 : 1.5}
                  opacity={d.source === 'auto' ? 0.6 : 1}
                  data-testid={`chart-point-${d.date}`} />
        </g>
      ))}

      {points.map((p, i) => (
        <text key={`x${i}`} x={scaleX(i)} y={H - 8} textAnchor="middle" fontSize="11"
              fill={i === sel?.i ? '#FFFFFF' : '#8A9099'}
              fontWeight={i === sel?.i ? 700 : 400}>
          {formatLabel(p.date, i)}
        </text>
      ))}

      {sel && tooltip && (
        <g data-testid="chart-tooltip">
          <rect x={tipX} y={tipY} width={tipW} height="56" rx="8"
                fill="#0B0D10" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" />
          <text x={tipX + 9} y={tipY + 15} fontSize="10" fill="#9CA3AF" letterSpacing="0.6">
            {tooltip.title.toUpperCase()}
          </text>
          <text x={tipX + 9} y={tipY + 31} fontSize="15" fill="#FFFFFF" fontWeight="800"
                data-testid="chart-tooltip-value">
            {formatValue(sel.value)}
            {selTarget > 0 && (
              <tspan fontSize="11" fill="#9CA3AF" fontWeight="500"> / {formatValue(selTarget)}</tspan>
            )}
          </text>
          <text x={tipX + 9} y={tipY + 46} fontSize="11" fill={tooltip.statusColor} fontWeight="700"
                data-testid="chart-tooltip-status">
            {tooltip.status}
          </text>
        </g>
      )}
    </svg>
  );
};

export default RecoveryChart;
